import React, { useState, useEffect, useRef } from "react";
import ArrowDownIcon from "../../../assets/svg/ArrowDownIcon"; // Adjust path as needed

const FilterPillsRow = ({
  boardGroupOptions = [],
  cancellationOptions = [],
  filters,
  onChangeFilters,
  isMobile,
}) => {
  const [showBoardDropdown, setShowBoardDropdown] = useState(false);
  const [showCancellationDropdown, setShowCancellationDropdown] = useState(false);

  const [selectedBoard, setSelectedBoard] = useState(filters.boardGroup || "");
  const [selectedCancellation, setSelectedCancellation] = useState(filters.cancellation || "");

  const boardDropdownRef = useRef(null);
  const cancellationDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (boardDropdownRef.current && !boardDropdownRef.current.contains(e.target)) {
        setShowBoardDropdown(false);
      }
      if (cancellationDropdownRef.current && !cancellationDropdownRef.current.contains(e.target)) {
        setShowCancellationDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectBoard = (option) => {
    setSelectedBoard(option);
    onChangeFilters({ ...filters, boardGroup: option });
    setShowBoardDropdown(false);
  };

  const handleSelectCancellation = (option) => {
    setSelectedCancellation(option);
    onChangeFilters({ ...filters, cancellation: option });
    setShowCancellationDropdown(false);
  };

  const renderButtonContent = (title, subtitle) => (
    <div style={{ textAlign: "left" }}>
      <div style={{ fontWeight: 700, color: "#003663", fontSize: "1rem" }}>{title}</div>
      <div style={{ fontWeight: 400, color: "#003663", fontSize: "0.9rem" }}>
        {subtitle || "all options"}
      </div>
    </div>
  );

  const dropdownBoxStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    zIndex: 999,
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    borderRadius: 20,
    padding: 6,
    marginTop: 4,
    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
  };

  const dropdownItemStyle = {
    padding: "10px 12px",
    cursor: "pointer",
    color: "#003663",
    borderRadius: 10,
    fontWeight: 500,
    transition: "background-color 0.3s ease-in-out, color 0.3s ease-in-out",
    "&:hover": {
      backgroundColor: "#003663",
      color: "#fff"
    }
  };

  // Add hover effect function
  const handleItemHover = (e) => {
    e.currentTarget.style.transition = "background-color 0.3s ease-in-out, color 0.3s ease-in-out";
    e.currentTarget.style.backgroundColor = "#003663";
    e.currentTarget.style.color = "#fff";
  };

  const handleItemLeave = (e) => {
    e.currentTarget.style.transition = "background-color 0.3s ease-in-out, color 0.3s ease-in-out";
    e.currentTarget.style.backgroundColor = "";
    e.currentTarget.style.color = "#003663";
  };

  const getItemStyle = (value, selectedValue) => ({
    ...dropdownItemStyle,
    backgroundColor: value === selectedValue ? "#003663" : "",
    color: value === selectedValue ? "#fff" : "#003663",
  });

  // ----------------- MOBILE -----------------
  if (isMobile) {
    return (
      <div className="d-flex flex-column gap-3 mb-4">
        {/* Meals */}
        <div ref={boardDropdownRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowBoardDropdown((prev) => !prev)}
            className="w-100 d-flex justify-content-between align-items-center px-4 py-3"
            style={{
              backgroundColor: "#fff",
              border: "2px solid #003663",
              borderRadius: 24,
              boxShadow: "0 4px 8px rgba(0,0,0,0.06)",
              color: "#003663",
            }}
          >
            {renderButtonContent("Meals", selectedBoard)}
            <ArrowDownIcon />
          </button>
          {showBoardDropdown && (
            <div style={dropdownBoxStyle}>
              <div 
                style={getItemStyle("", selectedBoard)} 
                onClick={() => handleSelectBoard("")}
                onMouseEnter={handleItemHover}
                onMouseLeave={(e) => {
                  if ("" !== selectedBoard) {
                    handleItemLeave(e);
                  }
                }}
              >
                All
              </div>
              {boardGroupOptions.map((opt) => (
                <div
                  key={opt}
                  style={getItemStyle(opt, selectedBoard)}
                  onClick={() => handleSelectBoard(opt)}
                  onMouseEnter={handleItemHover}
                  onMouseLeave={(e) => {
                    if (opt !== selectedBoard) {
                      handleItemLeave(e);
                    }
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cancellation */}
        <div ref={cancellationDropdownRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowCancellationDropdown((prev) => !prev)}
            className="w-100 d-flex justify-content-between align-items-center px-4 py-3"
            style={{
              backgroundColor: "#fff",
              border: "2px solid #003663",
              borderRadius: 24,
              boxShadow: "0 4px 8px rgba(0,0,0,0.06)",
              color: "#003663",
            }}
          >
            {renderButtonContent("Cancellation", selectedCancellation)}
            <ArrowDownIcon />
          </button>
          {showCancellationDropdown && (
            <div style={dropdownBoxStyle}>
              <div 
                style={getItemStyle("", selectedCancellation)}
                onClick={() => handleSelectCancellation("")}
                onMouseEnter={handleItemHover}
                onMouseLeave={(e) => {
                  if ("" !== selectedCancellation) {
                    handleItemLeave(e);
                  }
                }}
              >
                All
              </div>
              {cancellationOptions.map((opt) => (
                <div
                  key={opt}
                  style={getItemStyle(opt, selectedCancellation)}
                  onClick={() => handleSelectCancellation(opt)}
                  onMouseEnter={handleItemHover}
                  onMouseLeave={(e) => {
                    if (opt !== selectedCancellation) {
                      handleItemLeave(e);
                    }
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ----------------- DESKTOP -----------------
  return (
    <div
      className="mb-4"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16,
        width: "100%",
      }}
    >
      {/* Meals */}
      <div ref={boardDropdownRef} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setShowBoardDropdown((prev) => !prev)}
          className="w-100 d-flex justify-content-between align-items-center px-4 py-3"
          style={{
            backgroundColor: "#fff",
            border: "2px solid #003663",
            borderRadius: 24,
            boxShadow: "0 4px 8px rgba(0,0,0,0.06)",
            color: "#003663",
            height: "100%",
          }}
        >
          {renderButtonContent("Meals", selectedBoard)}
          <ArrowDownIcon />
        </button>
        {showBoardDropdown && (
          <div style={dropdownBoxStyle}>
            <div 
              style={getItemStyle("", selectedBoard)}
              onClick={() => handleSelectBoard("")}
              onMouseEnter={handleItemHover}
              onMouseLeave={(e) => {
                if ("" !== selectedBoard) {
                  handleItemLeave(e);
                }
              }}
            >
              All
            </div>
            {boardGroupOptions.map((opt) => (
              <div
                key={opt}
                style={getItemStyle(opt, selectedBoard)}
                onClick={() => handleSelectBoard(opt)}
                onMouseEnter={handleItemHover}
                onMouseLeave={(e) => {
                  if (opt !== selectedBoard) {
                    handleItemLeave(e);
                  }
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancellation */}
      <div ref={cancellationDropdownRef} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setShowCancellationDropdown((prev) => !prev)}
          className="w-100 d-flex justify-content-between align-items-center px-4 py-3"
          style={{
            backgroundColor: "#fff",
            border: "2px solid #003663",
            borderRadius: 24,
            boxShadow: "0 4px 8px rgba(0,0,0,0.06)",
            color: "#003663",
            height: "100%",
          }}
        >
          {renderButtonContent("Cancellation", selectedCancellation)}
          <ArrowDownIcon />
        </button>
        {showCancellationDropdown && (
          <div style={dropdownBoxStyle}>
            <div 
              style={getItemStyle("", selectedCancellation)}
              onClick={() => handleSelectCancellation("")}
              onMouseEnter={handleItemHover}
              onMouseLeave={(e) => {
                if ("" !== selectedCancellation) {
                  handleItemLeave(e);
                }
              }}
            >
              All
            </div>
            {cancellationOptions.map((opt) => (
              <div
                key={opt}
                style={getItemStyle(opt, selectedCancellation)}
                onClick={() => handleSelectCancellation(opt)}
                onMouseEnter={handleItemHover}
                onMouseLeave={(e) => {
                  if (opt !== selectedCancellation) {
                    handleItemLeave(e);
                  }
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPillsRow;
